import {
  ServerOp,
  type ServerMessage,
  decodeServerMessage,
  encodeHello,
  encodeInput,
  encodeJoinQueue,
  encodeLeave,
  encodePing,
  type PlayerState,
  type ItemState,
} from "./protocol";

export type ArenaStatus = "idle" | "connecting" | "queued" | "playing" | "finished" | "error";

export interface ArenaSnapshot {
  tick: number;
  receivedAt: number;
  players: PlayerState[];
  items: ItemState[];
}

export interface ArenaCallbacks {
  onStatus?: (status: ArenaStatus, message?: string) => void;
  onJoined?: (playerId: number, duration: number) => void;
  onSnapshot?: (snapshot: ArenaSnapshot) => void;
  onEat?: (playerId: number, itemId: number) => void;
  onResult?: (rank: number, score: number, coins: number) => void;
}

export class ArenaClient {
  private socket?: WebSocket;
  private sequence = 0;
  private pingTimer?: number;
  private closed = false;
  readonly snapshots: ArenaSnapshot[] = [];
  playerId = 0;

  constructor(private readonly callbacks: ArenaCallbacks = {}) {}

  connect() {
    this.close();
    this.closed = false;
    this.callbacks.onStatus?.("connecting");
    this.socket = new WebSocket(arenaUrl());
    this.socket.binaryType = "arraybuffer";
    this.socket.addEventListener("open", () => {
      const deviceId = crypto.randomUUID();
      this.send(encodeHello(deviceId));
      this.send(encodeJoinQueue());
      this.callbacks.onStatus?.("queued", "Suche Spieler …");
      this.pingTimer = window.setInterval(() => this.send(encodePing(++this.sequence)), 5000);
    });
    this.socket.addEventListener("message", (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      this.handle(decodeServerMessage(event.data));
    });
    this.socket.addEventListener("close", () => {
      if (!this.closed) this.callbacks.onStatus?.("error", "Arena-Verbindung getrennt");
    });
    this.socket.addEventListener("error", () => this.callbacks.onStatus?.("error", "Arena nicht erreichbar"));
  }

  sendTarget(x: number, z: number) {
    this.send(encodeInput(++this.sequence, x, z));
  }

  samplePlayers(renderTime = performance.now() - 100): PlayerState[] {
    if (this.snapshots.length < 2) return this.snapshots.at(-1)?.players ?? [];
    const b = this.snapshots.at(-1)!;
    const a = this.snapshots.at(-2)!;
    const span = Math.max(1, b.receivedAt - a.receivedAt);
    const alpha = Math.max(0, Math.min(1.25, (renderTime - a.receivedAt) / span));
    const previous = new Map(a.players.map((player) => [player.id, player]));
    return b.players.map((player) => {
      const from = previous.get(player.id) ?? player;
      return {
        ...player,
        x: from.x + (player.x - from.x) * alpha,
        z: from.z + (player.z - from.z) * alpha,
        size: from.size + (player.size - from.size) * Math.min(1, alpha),
      };
    });
  }

  close() {
    this.closed = true;
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    if (this.socket?.readyState === WebSocket.OPEN) this.send(encodeLeave());
    this.socket?.close();
    this.socket = undefined;
  }

  private send(frame: Uint8Array) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(frame);
  }

  private handle(message: ServerMessage) {
    switch (message.op) {
      case ServerOp.JoinedRoom:
        this.playerId = message.playerId;
        this.callbacks.onStatus?.("playing");
        this.callbacks.onJoined?.(message.playerId, message.duration);
        break;
      case ServerOp.Snapshot: {
        const snapshot = { tick: message.tick, receivedAt: performance.now(), players: message.players, items: message.items };
        this.snapshots.push(snapshot);
        if (this.snapshots.length > 3) this.snapshots.shift();
        this.callbacks.onSnapshot?.(snapshot);
        break;
      }
      case ServerOp.EventEat:
        this.callbacks.onEat?.(message.playerId, message.itemId);
        break;
      case ServerOp.Result:
        this.callbacks.onStatus?.("finished");
        this.callbacks.onResult?.(message.rank, message.totalScore, message.coinsDelta);
        break;
      case ServerOp.Error:
        this.callbacks.onStatus?.("error", message.message);
        break;
    }
  }
}

/** The native app is served from a local origin, so it talks to the live arena. */
export function arenaUrl(): string {
  const native = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
  if (native) return "wss://holegame.chillwebgames.com/v1/ws/arena";
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${location.host}/v1/ws/arena`;
}
